import { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  AcademicWorks,
  BasicInformation,
  KeywordCloud,
  VideoPresentations,
} from "./Components";
import styles from "./FacultyDetail.module.scss";
import RelatedFaculty from "./Components/RelatedFaculty";
import CollaboratorMap from "./Components/CollaboratorMap";
import { useSearchParams, useParams } from 'react-router-dom';

const FacultyDetail = () => {
  const { id } = useParams();
  const [institutionId, setInstitutionId] = useState(null);
  const [facultyInfo, setFacultyInfo] = useState({
    name: "",
    position: "",
    department: "",
    institution: "",
  });
  const [videoPresentations, setVideoPresentations] = useState([]);
  const [publicationVerify, setPublicationVerify] = useState(true);
  const [tab, setTab] = useState("publications");
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [sliderValue, setSliderValue] = useState(null);
  const [tabDisableInformation, setTabDisableInformation] = useState({
    has_collaborators: false,
    has_grants: false,
    has_publications: false,
  });
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';

  const currentUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
  
  const generateTitle = () => {
    // Advanced title logic (commented out for future version)
    
    if (!facultyInfo.name) return "Faculty Profile | Academic Online";
    
    const name = facultyInfo.name;
    const position = facultyInfo.position;
    const department = facultyInfo.department;
    const institution = facultyInfo.institution;
    const suffix = " | Academic Online";
    const maxLength = 60;

    // Full title: Name — Position at Department, Institution | Academic Online
    let affiliationFull = department && institution ? `${department}, ${institution}` : (department || institution);
    let titleFull = position && affiliationFull ? `${name} — ${position} at ${affiliationFull}` :
                    affiliationFull ? `${name} at ${affiliationFull}` :
                    position ? `${name} — ${position}` : name;
    
    if ((titleFull + suffix).length <= maxLength) return titleFull + suffix;

    // Fallback 1: Drop department
    let titleNoDept = position && institution ? `${name} — ${position} at ${institution}` :
                      institution ? `${name} at ${institution}` :
                      position ? `${name} — ${position}` : name;
                      
    if ((titleNoDept + suffix).length <= maxLength) return titleNoDept + suffix;

    // Fallback 2: Name at Institution
    let titleNameInst = institution ? `${name} at ${institution}` : name;
    if ((titleNameInst + suffix).length <= maxLength) return titleNameInst + suffix;

    // Fallback 3: Name only
    return name + suffix;
      // // Original simple logic
    // return facultyInfo.name ? `${facultyInfo.name} - Faculty Profile | Academic Explorer` : "Faculty Profile & Research Details | Academic Explorer";
  };
  
  const generateDescription = () => {
    const defaultDesc = "View detailed faculty profile including research interests, publications, academic works, and collaboration network.";
    if (!facultyInfo.name) return defaultDesc;
    
    const name = facultyInfo.name;
    const institution = facultyInfo.institution;
    const department = facultyInfo.department;
    const maxLength = 160;

    let descFull = `Explore ${name}'s academic profile at ${department}, ${institution}. Research interests, publications, grants, and collaboration network.`;
    if (descFull.length <= maxLength) return descFull;

    let descNoDept = `Explore ${name}'s academic profile at ${institution}. Research interests, publications, grants, and collaboration network.`;
    if (descNoDept.length <= maxLength) return descNoDept;

    let descNameOnly = `Explore ${name}'s academic profile including research interests, publications, grants, and collaboration network.`;
    if (descNameOnly.length <= maxLength) return descNameOnly;

    return `${descNameOnly.substring(0, 157)}...`;
  };

  return (
    <div className={styles["faculty-detail"]}>
      {process.env.NODE_ENV !== 'development' && (
        <Helmet>
          <title>{generateTitle()}</title>
          <meta name="description" content={generateDescription()} />
          <link rel="canonical" href={currentUrl} />
          <meta property="og:url" content={currentUrl} />
          <meta property="og:title" content={generateTitle()} />
          <meta property="og:description" content={generateDescription()} />
        </Helmet>
      )}
      <BasicInformation
        setPublicationVerify={setPublicationVerify}
        setInstitutionId={setInstitutionId}
        setVideoPresentations={setVideoPresentations}
        setTabDisableInformation={setTabDisableInformation}
        setFacultyInfo={setFacultyInfo}
      />
      <section className={styles["academic-result"]}>
        <KeywordCloud
          selectedKeywords={selectedKeywords}
          setSelectedKeywords={setSelectedKeywords}
          sliderValue={sliderValue}
          setSliderValue={setSliderValue}
          publicationVerify={publicationVerify}
          tab={tab}
        />
        <CollaboratorMap />
        <VideoPresentations videos={videoPresentations} />
        <AcademicWorks
          tab={tab}
          setTab={setTab}
          selectedKeywords={selectedKeywords}
          setSelectedKeywords={setSelectedKeywords}
          sliderValue={sliderValue}
          institutionId={institutionId}
          publicationVerify={publicationVerify}
          setPublicationVerify={setPublicationVerify}
          tabDisableInformation={tabDisableInformation}
        />
        {!isPreview && <RelatedFaculty />}
      </section>
    </div>
  );
};

export default FacultyDetail;
